import React, { useContext, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { Table } from 'react-bootstrap';
import { AppContext } from '../../App';
import {
  formatOrderContactUpdatedAt,
  groupOrderRequestsBySupplier,
  patchOrderRequest,
} from '../../clients/order_request_client';
import { showToast } from '../../config_and_helpers/helpers';
import { logError, logInfo } from '../../config_and_helpers/logger';
import OrderRequestDetailModal from './OrderRequestDetailModal';
import OrderRequestSupplierGroupQuoteButton from './OrderRequestSupplierGroupQuoteButton';

const LOG_CONTEXT = 'OrderRequestsList';

/**
 * Fulfill or cancel one open request from the inbox row.
 * Sends status only, so notes and the linked order stay as stored.
 *
 * @param {Object} props
 * @param {Object} props.row
 * @param {function} props.onRequestUpdated
 */
const OrderRequestRowActions = ({ row, onRequestUpdated }) => {
  const { token } = useContext(AppContext);
  const [pendingStatus, setPendingStatus] = useState(null);

  const handleStatus = async status => {
    if (!row?.id || pendingStatus) return;
    setPendingStatus(status);
    logInfo(LOG_CONTEXT, 'row status patch', { requestId: row.id, status });
    const response = await patchOrderRequest(token, row.id, { status });
    setPendingStatus(null);
    if (response?.success) {
      showToast(
        `Order request ${response.data?.request_ref || row.request_ref || ''} updated`,
        'success'
      );
      onRequestUpdated?.(response.data);
      return;
    }
    logError(LOG_CONTEXT, 'row status patch failed', {
      requestId: row.id,
      status,
      message: response?.messages?.[0] || 'Update failed',
    });
    showToast(
      response?.messages?.[0] || 'Update failed',
      'error',
      'top-center',
      4000
    );
  };

  if (row.status !== 'REQUESTED') return null;

  return (
    <div className="d-flex justify-content-center flex-wrap gap-2">
      <Button
        variant="success"
        size="sm"
        disabled={pendingStatus != null}
        onClick={() => handleStatus('FULFILLED')}
      >
        {pendingStatus === 'FULFILLED' ? 'Saving…' : 'Fulfill'}
      </Button>
      <Button
        variant="outline-danger"
        size="sm"
        disabled={pendingStatus != null}
        onClick={() => handleStatus('CANCELLED')}
      >
        {pendingStatus === 'CANCELLED' ? 'Saving…' : 'Cancel'}
      </Button>
    </div>
  );
};

/**
 * One order-request row. The ref opens the detail modal.
 *
 * @param {Object} props
 * @param {Object} props.row
 * @param {function} props.onRequestUpdated
 * @param {boolean} props.showRowActions
 */
const OrderRequestRow = ({ row, onRequestUpdated, showRowActions }) => (
  <tr className="text-center align-middle">
    <td>
      <OrderRequestDetailModal
        requestRow={row}
        onUpdated={onRequestUpdated}
      />
    </td>
    <td>
      {(row.product_names || []).join(', ') ||
        (row.lines?.[0]?.product_name ?? '—')}
    </td>
    <td>{row.project_name || '—'}</td>
    <td>{row.requested_by_name || '—'}</td>
    <td>
      {row.contact_name || row.contact_email
        ? `${row.contact_name || ''}${
            row.contact_email ? ` <${row.contact_email}>` : ''
          }`
        : '—'}
    </td>
    <td>{formatOrderContactUpdatedAt(row.created_at) || '—'}</td>
    <td>{row.status}</td>
    {showRowActions ? (
      <td>
        <OrderRequestRowActions
          row={row}
          onRequestUpdated={onRequestUpdated}
        />
      </td>
    ) : null}
  </tr>
);

/**
 * Procurement inbox as one table.
 *
 * Open groups rows by supplier and keeps Request quotes on that supplier row,
 * plus Fulfill and Cancel on each request. History is a flat list and does
 * not offer those actions.
 *
 * @param {Object} props
 * @param {Array} props.requests
 * @param {function} props.onRequestUpdated
 * @param {boolean} [props.hasGlobalContact]
 * @param {string} [props.globalContactDisabledReason]
 * @param {function} [props.onConfigureContact]
 * @param {boolean} [props.showSupplierQuotes] - Open tab only. Hidden for Viewers.
 * @param {boolean} [props.showRowActions] - Fulfill and Cancel. Open tab only.
 */
const OrderRequestsList = ({
  requests = [],
  onRequestUpdated,
  hasGlobalContact = false,
  globalContactDisabledReason = '',
  onConfigureContact,
  showSupplierQuotes = true,
  showRowActions = false,
}) => {
  const columnCount = showRowActions ? 8 : 7;
  const groups = useMemo(
    () => groupOrderRequestsBySupplier(requests),
    [requests]
  );

  return (
    <Table striped bordered hover className="mb-0">
      <thead>
        <tr className="text-center">
          <th>Ref</th>
          <th>Products</th>
          <th>Project</th>
          <th>Requested by</th>
          <th>Contact</th>
          <th>Created</th>
          <th>Status</th>
          {showRowActions ? <th>Actions</th> : null}
        </tr>
      </thead>
      <tbody>
        {showSupplierQuotes
          ? groups.map(group => (
              <React.Fragment key={group.supplierId ?? group.supplierName}>
                <tr>
                  <td colSpan={columnCount} className="text-start">
                    <div className="d-flex align-items-center gap-3">
                      <span className="fw-semibold">
                        {group.supplierName}
                        <span className="ms-2 text-muted small fw-normal">
                          ({group.requests.length} request
                          {group.requests.length === 1 ? '' : 's'})
                        </span>
                      </span>
                      <OrderRequestSupplierGroupQuoteButton
                        group={group}
                        hasGlobalContact={hasGlobalContact}
                        contactDisabledReason={globalContactDisabledReason}
                        onConfigureContact={onConfigureContact}
                      />
                    </div>
                  </td>
                </tr>
                {group.requests.map(row => (
                  <OrderRequestRow
                    key={row.id}
                    row={row}
                    onRequestUpdated={onRequestUpdated}
                    showRowActions={showRowActions}
                  />
                ))}
              </React.Fragment>
            ))
          : requests.map(row => (
              <OrderRequestRow
                key={row.id}
                row={row}
                onRequestUpdated={onRequestUpdated}
                showRowActions={showRowActions}
              />
            ))}
      </tbody>
    </Table>
  );
};

export default OrderRequestsList;
